
import { CheckCircle2, XCircle } from 'lucide-react';
import { Plan } from '../types';
import { Button } from "@/components/ui/button";

interface ComparisonTableProps {
  plans: Plan[];
  onClearSelection: () => void;
  className?: string;
}

export const ComparisonTable = ({ 
  plans, 
  onClearSelection,
  className = '' 
}: ComparisonTableProps) => {
  if (plans.length === 0) {
    return null;
  }
  
  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('da-DK', {
      style: 'currency',
      currency: 'DKK',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(price);
  };
  
  return (
    <div id="comparison" className={`rounded-lg border border-border overflow-hidden bg-card shadow-sm ${className}`}>
      <div className="bg-muted/40 p-4 flex justify-between items-center">
        <h3 className="font-semibold">Sammenligner {plans.length} Abonnement{plans.length !== 1 ? 'er' : ''}</h3>
        <Button 
          variant="outline" 
          size="sm"
          onClick={onClearSelection}
        >
          Ryd Valg
        </Button>
      </div>
      
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border">
              <th className="py-4 px-6 text-left font-medium text-muted-foreground w-1/4">Funktioner</th>
              {plans.map(plan => (
                <th key={plan.id} className="py-4 px-6 text-left font-medium">
                  <div className="mb-1 font-semibold text-base">{plan.name}</div>
                  <div className="text-muted-foreground text-xs">{plan.provider}</div>
                </th>
              ))}
            </tr>
          </thead>
          
          <tbody>
            {/* Price */}
            <tr className="border-b border-border">
              <td className="py-4 px-6 font-medium">Månedlig Pris</td>
              {plans.map(plan => (
                <td key={plan.id} className="py-4 px-6">
                  <div className="font-semibold">{formatPrice(plan.price)}/md</div>
                  {plan.price_label && (
                    <div className="text-xs text-muted-foreground">{plan.price_label}</div>
                  )}
                </td>
              ))}
            </tr>
            
            {/* Data Allowance */}
            <tr className="border-b border-border">
              <td className="py-4 px-6 font-medium">Data</td>
              {plans.map(plan => (
                <td key={plan.id} className="py-4 px-6 font-semibold">
                  {plan.data_allowance}
                </td>
              ))}
            </tr>
            
            {/* Contract Length */}
            <tr className="border-b border-border">
              <td className="py-4 px-6 font-medium">Bindingsperiode</td>
              {plans.map(plan => (
                <td key={plan.id} className="py-4 px-6">
                  {plan.contract_length}
                </td>
              ))}
            </tr>
            
            {/* 5G Support */}
            <tr className="border-b border-border">
              <td className="py-4 px-6 font-medium">5G Netværk</td>
              {plans.map(plan => (
                <td key={plan.id} className="py-4 px-6">
                  {plan.has_5g ? (
                    <CheckCircle2 className="h-5 w-5 text-success-500" />
                  ) : (
                    <XCircle className="h-5 w-5 text-muted-foreground" />
                  )}
                </td>
              ))}
            </tr>
            
            {/* Minutes & Texts */}
            <tr className="border-b border-border">
              <td className="py-4 px-6 font-medium">Opkald & SMS</td>
              {plans.map(plan => (
                <td key={plan.id} className="py-4 px-6">
                  {plan.minutes} minutter<br />
                  {plan.texts} SMS'er
                </td>
              ))}
            </tr>
            
            {/* Roaming */}
            <tr className="border-b border-border">
              <td className="py-4 px-6 font-medium">Roaming</td>
              {plans.map(plan => (
                <td key={plan.id} className="py-4 px-6">
                  {plan.roaming}
                </td>
              ))}
            </tr>
            
            {/* Key Features */}
            <tr className="border-b border-border">
              <td className="py-4 px-6 font-medium">Fordele</td>
              {plans.map(plan => (
                <td key={plan.id} className="py-4 px-6">
                  <ul className="space-y-2">
                    {plan.features.map((feature, index) => (
                      <li key={index} className="flex items-start">
                        <CheckCircle2 className="h-4 w-4 text-success-500 mr-2 mt-0.5 flex-shrink-0" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                </td>
              ))}
            </tr>
            
            {/* Rating */}
            <tr className="border-b border-border">
              <td className="py-4 px-6 font-medium">Kundebedømmelse</td>
              {plans.map(plan => (
                <td key={plan.id} className="py-4 px-6">
                  <div className="flex items-center">
                    <div className="flex -space-x-1 mr-2">
                      {Array(5).fill(0).map((_, i) => (
                        <div 
                          key={i} 
                          className={`h-2 w-2 rounded-full border border-white ${
                            i < Math.floor(plan.rating) ? 'bg-brand-500' : 'bg-gray-200'
                          }`}
                        ></div>
                      ))}
                    </div>
                    <span className="font-medium">{plan.rating.toFixed(1)}</span>
                    <span className="text-xs text-muted-foreground ml-1">({plan.review_count})</span>
                  </div>
                </td>
              ))}
            </tr>
            
            {/* Action Button */}
            <tr>
              <td className="py-4 px-6"></td>
              {plans.map(plan => (
                <td key={plan.id} className="py-4 px-6">
                  <Button asChild className="w-full">
                    <a href={plan.link} target="_blank" rel="noopener noreferrer">
                      Se Tilbud
                    </a>
                  </Button>
                </td>
              ))}
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
};
